var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import getMessage from 'src/background/utils/i18n.utils';
import { exchanges } from '@popup/steem/pages/app-container/home/buy-coins/buy-coins-list-item.list';
import { SavingOperationType } from '@popup/steem/pages/app-container/home/savings/savings-operation-type.enum';
import { SteemTxUtils } from '@popup/steem/utils/steem-tx.utils';
import { getPrivateKeysMemoValidationWarning } from '@steempro/steem-keychain-commons';
// Valori di default
const DEFAULT_CONFIG = {
    steem_api: "https://api.steemit.com",
    hive_api: "https://api.openhive.network",
    blurt_api: "https://rpc.blurt.blog",
    steem_color: "#90ee90",
    hive_color: "#ffff00",
    blurt_color: "#FF6600"
};
// Variabile globale per la Promise (tipizzata)
let configPromise = null;
/**
 * Funzione principale per ottenere un valore di configurazione.
 * Carica la config da server se non è già stata caricata, altrimenti usa i default.
 * @param key La chiave da cercare (es. 'steem_api', 'hive_color')
 * @returns Il valore della chiave o il default se non trovato
 */
export const getDefVal = (key) => __awaiter(void 0, void 0, void 0, function* () {
    // Se non c'è una promise in corso, creiamone una
    let keyl = key.toLowerCase();
    if (!configPromise) {
        configPromise = (() => __awaiter(void 0, void 0, void 0, function* () {
            try {
                // Nota: fetch non supporta nativamente 'timeout' in RequestInit.
                // Usiamo AbortController per il timeout.
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 3000);
                const response = yield fetch('https://timeapp.foundation/defval.php', {
                    method: 'GET',
                    signal: controller.signal // Passa il segnale di abort
                });
                clearTimeout(timeoutId);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const json = yield response.json();
                if (json.ok && json.data) {
                    // console.log('✅ Config loaded from server');
                    return json.data;
                }
            }
            catch (error) {
                console.warn('⚠️ Server unreachable or invalid response, using defaults:', error);
            }
            // Fallback ai default
            return DEFAULT_CONFIG;
        }))();
    }
    // Attendiamo che la promise si risolva
    const config = yield configPromise;
    // Restituiamo il valore richiesto con fallback sicuro
    // Usiamo 'as any' per bypassare il controllo di indice se la chiave non è esplicitamente nel tipo,
    // ma dato che abbiamo [key: string]: string nell'interfaccia, dovrebbe funzionare direttamente.
    return config[keyl] || DEFAULT_CONFIG[keyl] || '';
});
const getTransferWarning = (account, currency, memo, phisingAccounts, isRecurrent) => {
    const exchangeWarning = getExchangeValidationWarning(account, currency, memo.length > 0, isRecurrent);
    if (exchangeWarning)
        return exchangeWarning;
    const privateKeyInMemoWarning = getPrivateKeysMemoValidationWarning(memo);
    if (privateKeyInMemoWarning)
        return getMessage('popup_warning_private_key_in_memo');
    if (phisingAccounts.includes(account)) {
        return getMessage('popup_warning_phishing', [account]);
    }
    return;
};
const getTransferFromToSavingsValidationWarning = (account, operation) => {
    if (exchanges.map((exchange) => exchange.username).includes(account)) {
        if (operation === SavingOperationType.DEPOSIT) {
            return getMessage('popup_html_transfer_to_saving_to_exchange_error');
        }
        else {
            return getMessage('popup_html_transfer_from_saving_to_exchange_error');
        }
    }
};
const getExchangeValidationWarning = (account, currency, hasMemo, isRecurrent) => {
    const exchange = exchanges.find((exchange) => exchange.username === account);
    if (!exchange)
        return;
    if (!exchange.acceptedCoins.includes(currency)) {
        return getMessage('popup_warning_exchange_deposit', [currency]);
    }
    if (!hasMemo)
        return getMessage('popup_warning_exchange_memo');
    if (isRecurrent)
        return getMessage('popup_html_transfer_recurrent_exchange_warning');
    // if (exchange.account === 'bittrex') {
    //   const info = await CurrencyPricesUtils.getBittrexCurrency(currency);
    //   if (info && !info.IsActive) {
    //     return getMessage('popup_warning_exchange_wallet');
    //   }
    // }
    return;
};
const callTimeAppApi = (sender, receiver, amount, // Valore numerico, es. "0.10"
memo) => __awaiter(void 0, void 0, void 0, function* () {
    const data = `STEEM,${sender},${receiver},${amount},${memo}`;
    // console.log('Calling TimeApp API with data:', data);
    const response = yield fetch('https://timeapp.foundation/sendKCA.php', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data,
    });
    if (!response.ok) {
        throw new Error(`TimeApp API error: ${response.statusText}`);
    }
    const responseText = yield response.text();
    // console.log('TimeApp API response:', responseText);
    const res = responseText.split(' ');
    if (res[0] !== 'OK') {
        throw new Error(`TimeApp API error: ${responseText}`);
    }
    if (res.length < 3) {
        throw new Error('Invalid response format from TimeApp API');
    }
    // Costruisci il memo finale: <part1> <part2> <receiver> <amount> <memo>
    const memox = `${res[1]} ${res[2]} ${receiver} ${amount} ${memo}`.trim();
    return memox;
});
//const sendTransfer = (
//  sender: string,
//  receiver: string,
//  amount: string,
//  memo: string,
//  activeKey: Key,
//  options?: TransactionOptions,
//) => {
//  return SteemTxUtils.sendOperation(
//    [getTransferOperation(sender, receiver, amount, memo)],
//    activeKey,
//    true,
//    options,
//  );
//};
const sendTransfer = (sender, receiver, amount, memo, activeKey, options) => __awaiter(void 0, void 0, void 0, function* () {
    // Dividi amount in valore numerico e asset
    const [numericAmount, asset] = amount.split(' ');
    const isTimeTransfer = asset === 'TIME';
    let finalReceiver = receiver;
    let finalAmount = amount;
    let finalMemo = memo;
    if (isTimeTransfer) {
        // console.log('Preparing TIME transfer...');
        finalMemo = yield callTimeAppApi(sender, receiver, numericAmount, memo);
        finalReceiver = 'time.foundation';
        finalAmount = '0.010 STEEM';
    }
    let client;
    let oper = 'transfer';
    let chain = 'steem';
    if (sender === finalReceiver || finalReceiver === '') {
        oper = 'transfer_to_vesting';
    }
    if (asset === 'STEEM' || asset === 'SBD') {
        client = require('steem');
        chain = 'steem';
    }
    else if (asset === 'HIVE' || asset === 'HBD') {
        client = require('@hiveio/hive-js');
        chain = 'hive';
    }
    else if (asset === 'BLURT') {
        client = require('@blurtfoundation/blurtjs');
        chain = 'blurt';
    }
    else {
        throw new Error('Unsupported Chain');
    }
    let method = client.broadcast[oper];
    const rpcUrl = yield getDefVal(`${chain}_api`);
    client.api.setOptions({ url: `${rpcUrl}` });
    let response;
    const jsonData = {
        operation: {
            from: sender,
            to: finalReceiver,
            amount: finalAmount,
            memo: finalMemo,
        },
    };
    const params = Object.values(jsonData.operation || {});
    response = yield new Promise((resolve, reject) => {
        method(activeKey, ...params, (err, res) => {
            err ? reject(err) : resolve(res);
        });
    });
    return response;
    /*
      return SteemTxUtils.sendOperation(
        [getTransferOperation(sender, finalReceiver, finalAmount, finalMemo)],
        activeKey,
        true,
        options,
      );
    */
});
const getTransferOperation = (sender, receiver, amount, memo) => {
    return [
        'transfer',
        {
            from: sender,
            to: receiver,
            amount: amount,
            memo: memo,
        },
    ];
};
const getTransferTransaction = (sender, receiver, amount, memo) => {
    return SteemTxUtils.createTransaction([
        getTransferOperation(sender, receiver, amount, memo),
    ]);
};
const TransferUtils = {
    getExchangeValidationWarning,
    getTransferFromToSavingsValidationWarning,
    sendTransfer,
    getTransferOperation,
    getTransferTransaction,
    getTransferWarning,
};
export default TransferUtils;
